import { Navigate, useParams } from 'react-router-dom';
import { ActiveStorefrontScreen } from '@/features/website-templates/components/active-storefront-screen';
import { StorefrontRouteStates } from '@/features/website-templates/components/storefront-route-states';
import { useOpenedTemplateEditorData } from '@/features/website-templates/hooks/use-storefront-editor-data';
import { toActiveEditorState } from '@/features/website-templates/lib/editor-state';
import {
  isTemplateId,
  TEMPLATE_LIBRARY_PATH,
  type TemplateLibraryLocationState,
  type TemplateLibraryNotice,
} from '@/features/website-templates/lib/template-routes';

function BackToLibrary({
  notice = 'template-unavailable',
}: {
  readonly notice?: TemplateLibraryNotice;
}): React.JSX.Element {
  const state: TemplateLibraryLocationState = { notice };
  return <Navigate replace to={TEMPLATE_LIBRARY_PATH} state={state} />;
}

/**
 * `/storefront/templates/:templateId/edit` (epic #228 P3): the active
 * storefront of ONE template, named by the address. The rendered template
 * changes only when this address changes.
 *
 * The route reads the template's state first. A template with no saved
 * draft is not started by visiting its address: the merchant goes to the
 * library ("Use template" there asks first and creates the draft). A
 * template with a draft is opened (idempotent) before the editor shows, so a
 * save or a publish never targets an archived head.
 *
 * A template that cannot be opened here (not an id, unknown, not this
 * store's) sends the merchant to the library with a notice. The library is also where a newer server asks for a reload, so the
 * merchant never sees a blank screen. A network failure keeps the usual
 * error with Retry.
 */
export function StorefrontTemplateEditRoute(): React.JSX.Element {
  const { templateId } = useParams();
  if (!isTemplateId(templateId)) return <BackToLibrary />;
  // A new template id remounts the screen: nothing of the previous template survives.
  return <TemplateEditScreen key={templateId} templateId={templateId} />;
}

function TemplateEditScreen({ templateId }: { readonly templateId: string }): React.JSX.Element {
  // Reads first; opens an existing draft before anything is saved or published.
  const data = useOpenedTemplateEditorData(templateId);
  if (data.status === 'error' && data.runtimeUnavailable) return <BackToLibrary />;
  if (data.status !== 'ready') return <StorefrontRouteStates data={data} />;
  // No saved draft: nothing was created; first use starts in the library.
  if (data.state.head === null && data.state.templateId !== null) {
    return <BackToLibrary notice="not-started" />;
  }

  const state = toActiveEditorState(data.state);
  // The state must describe the template of the address, never another one.
  if (state === null || state.templateId.toLowerCase() !== templateId.toLowerCase()) {
    return <BackToLibrary />;
  }
  return (
    <ActiveStorefrontScreen
      storeId={data.storeId}
      storeSlug={data.storeSlug}
      templateId={state.templateId}
      state={state}
      reloadState={data.reload}
    />
  );
}
